import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Steps, Button, Row, Col, Spin, Typography, message } from "antd";
import moment from "moment";

import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";

import BasicInfoStep from "./steps/BasicInfoStep";
import DetailsStep from "./steps/DetailsStep";
import ComponentsStep from "./steps/ComponentsStep";
import PricingStep from "./steps/PricingStep";
import {
  validateBasicInfo,
  validateDetails,
  validateComponents,
  validatePricing,
  stepHasErrors,
} from "./steps/validators";

import {
  createPackage,
  updatePackage,
  uploadPackageImages,
  createQuickTrip,
  updateQuickTrip,
  uploadQuickTripImages,
  fetchPackageById,
  fetchQuickTripById,
  removePackageCustomization,
} from "../../features/packages/packagesThunk";
import {
  clearSelectedPackage,
  clearSelectedQuickTrip,
} from "../../features/packages/packages";

const { Title } = Typography;

const emptyForm = (listType) => ({
  listType,
  title: "",
  description: "",
  imageUrls: [],
  tags: [],
  location: { name: "", address: "", city: "", state: "", country: "" },
  organizerId: null,
  customizable: false,
  includesFlight: false,
  isInternationalFlight: false,

  quickFacts: [],
  inclusionsPackage: [],
  inclusionsQuickTrip: [],
  exclusions: [],
  includedProducts: [],
  highlights: [],
  importantInfo: [],
  cancellationPolicy: [],

  price: { amount: null, currency: "NGN" },
  occupancy: { travellers_per_room: null, max_travellers_per_booking: null },
  groupSize: null,
  duration: null,
  startDate: null,
  endDate: null,
  schedule: {
    runs_daily: true,
    min_booking_lead_hours: 24,
    pickup_time: null,
    return_time: null,
  },
  verified: false,
});

// strip blank entries out of a plain-string list before sending it
const cleanStrings = (arr = []) => arr.map((s) => s?.trim()).filter(Boolean);

const buildPayload = (form, entries) => {
  const base = {
    title: form.title,
    description: form.description || null,
    tags: cleanStrings(form.tags),
    image_urls: cleanStrings(form.imageUrls),
    included_products: cleanStrings(form.includedProducts),
    price: { amount: Number(form.price.amount) || 0, currency: form.price.currency },
    quick_facts: form.quickFacts.filter((f) => f.label),
    start_date: form.startDate ? form.startDate.toISOString() : null,
    end_date: form.endDate ? form.endDate.toISOString() : null,
    exclusions: cleanStrings(form.exclusions),
    location: form.location,
    cancellation_policy: cleanStrings(form.cancellationPolicy),
    // Always sent (null included) so clearing the select on edit unassigns.
    organizer_id: form.organizerId || null,
  };

  if (form.listType === "QUICK_TRIP") {
    return {
      ...base,
      duration: Number(form.duration) || 1,
      schedule: {
        runs_daily: !!form.schedule.runs_daily,
        min_booking_lead_hours: Number(form.schedule.min_booking_lead_hours) || 0,
        pickup_time: form.schedule.pickup_time
          ? form.schedule.pickup_time.format("HH:mm")
          : undefined,
        return_time: form.schedule.return_time
          ? form.schedule.return_time.format("HH:mm")
          : undefined,
      },
      group_size: Number(form.groupSize) || 1,
      highlights: cleanStrings(form.highlights),
      inclusions: cleanStrings(form.inclusionsQuickTrip),
      important_info: cleanStrings(form.importantInfo),
    };
  }

  const customization_options = entries.length
    ? {
        create: entries
          .filter((e) => e.mode === "existing" ? e.customization_option_id : e.newName)
          .map((e) => ({
            ...(e.mode === "existing"
              ? { customization_option_id: e.customization_option_id }
              : {
                  customization_option: {
                    create: {
                      category: e.category,
                      name: e.newName,
                      ...(e.mediaUrl?.trim() ? { media_url: e.mediaUrl.trim() } : {}),
                    },
                  },
                }),
            price_delta: Number(e.price_delta) || 0,
            is_default: !!e.is_default,
          })),
      }
    : undefined;

  return {
    ...base,
    occupancy: {
      travellers_per_room: Number(form.occupancy.travellers_per_room) || 1,
      max_travellers_per_booking: Number(form.occupancy.max_travellers_per_booking) || 1,
    },
    customizable: !!form.customizable,
    includes_flight: !!form.includesFlight,
    is_international_flight: !!form.includesFlight && !!form.isInternationalFlight,
    verified: !!form.verified,
    inclusions: form.inclusionsPackage.filter((i) => i.label),
    ...(customization_options ? { customization_options } : {}),
  };
};

const PackageWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const initialType = searchParams.get("type") === "QUICK_TRIP" ? "QUICK_TRIP" : "PACKAGE";

  const { selectedPackage, selectedQuickTrip, packagesLoading, quickTripsLoading, packageActionLoading, quickTripActionLoading } =
    useSelector((state) => state.packages);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm(initialType));
  const [entries, setEntries] = useState([]);
  const [existingLinks, setExistingLinks] = useState([]);
  const [stepErrors, setStepErrors] = useState({});
  const [pendingImageFiles, setPendingImageFiles] = useState([]);

  useEffect(() => {
    if (!isEdit) return;
    if (initialType === "PACKAGE") dispatch(fetchPackageById(id));
    else dispatch(fetchQuickTripById(id));
    return () => {
      dispatch(clearSelectedPackage());
      dispatch(clearSelectedQuickTrip());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, initialType]);

  useEffect(() => {
    if (!isEdit) return;
    const record = initialType === "PACKAGE" ? selectedPackage : selectedQuickTrip;
    if (!record) return;

    setForm({
      listType: initialType,
      title: record.title || "",
      description: record.description || "",
      imageUrls: record.image_urls || [],
      tags: record.tags || [],
      location: record.location || { name: "", address: "", city: "", state: "", country: "" },
      organizerId: record.organizer_id || null,
      customizable: !!record.customizable,
      includesFlight: !!record.includes_flight,
      isInternationalFlight: !!record.is_international_flight,

      quickFacts: record.quick_facts || [],
      inclusionsPackage: initialType === "PACKAGE" ? (record.inclusions || []) : [],
      inclusionsQuickTrip: initialType === "QUICK_TRIP" ? (record.inclusions || []) : [],
      exclusions: record.exclusions || [],
      includedProducts: record.included_products || [],
      highlights: record.highlights || [],
      importantInfo: record.important_info || [],
      cancellationPolicy: record.cancellation_policy || [],

      price: record.price || { amount: null, currency: "NGN" },
      occupancy: record.occupancy || { travellers_per_room: null, max_travellers_per_booking: null },
      groupSize: record.group_size ?? null,
      duration: record.duration ?? null,
      startDate: record.start_date ? moment(record.start_date) : null,
      endDate: record.end_date ? moment(record.end_date) : null,
      schedule: record.schedule
        ? {
            runs_daily: !!record.schedule.runs_daily,
            min_booking_lead_hours: record.schedule.min_booking_lead_hours ?? 24,
            pickup_time: record.schedule.pickup_time
              ? moment(record.schedule.pickup_time, "HH:mm")
              : null,
            return_time: record.schedule.return_time
              ? moment(record.schedule.return_time, "HH:mm")
              : null,
          }
        : { runs_daily: true, min_booking_lead_hours: 24, pickup_time: null, return_time: null },
      verified: !!record.verified,
    });

    if (initialType === "PACKAGE") setExistingLinks(record.customization_options || []);
    setPendingImageFiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage, selectedQuickTrip]);

  const isQuickTrip = form.listType === "QUICK_TRIP";
  const actionLoading = isQuickTrip ? quickTripActionLoading : packageActionLoading;
  const loadingRecord = isEdit && (isQuickTrip ? quickTripsLoading : packagesLoading) &&
    !(isQuickTrip ? selectedQuickTrip : selectedPackage);

  const stepTitles = useMemo(
    () =>
      isQuickTrip
        ? ["Basic Information", "Details", "Pricing"]
        : ["Basic Information", "Details", "Components", "Pricing"],
    [isQuickTrip],
  );

  const validateStep = (key) => {
    if (key === "Basic Information") return validateBasicInfo(form, pendingImageFiles);
    if (key === "Details") return validateDetails(form);
    if (key === "Components") return validateComponents(entries);
    if (key === "Pricing") return validatePricing(form);
    return {};
  };

  const goToStep = (nextStep) => {
    const errors = validateStep(stepTitles[step]);
    if (stepHasErrors(errors)) {
      setStepErrors(errors);
      message.error("Please fix the highlighted fields before continuing");
      return;
    }
    setStepErrors({});
    setStep(nextStep);
  };

  const handleRemoveExisting = async (optionId) => {
    const result = await dispatch(removePackageCustomization({ packageId: id, optionId }));
    if (!result.error) {
      setExistingLinks((prev) =>
        prev.filter((l) => (l.customization_option?.id || l.customization_option_id) !== optionId),
      );
    }
  };

  const handleSubmit = async () => {
    const errors = validateStep(stepTitles[step]);
    if (stepHasErrors(errors)) {
      setStepErrors(errors);
      message.error("Please fix the highlighted fields before saving");
      return;
    }

    const payload = buildPayload(form, entries);

    const action = isEdit
      ? isQuickTrip
        ? updateQuickTrip({ id, data: payload })
        : updatePackage({ id, data: payload })
      : isQuickTrip
        ? createQuickTrip(payload)
        : createPackage(payload);

    const result = await dispatch(action);
    if (result.error) return;

    if (pendingImageFiles.length) {
      const savedId = isEdit ? id : result.payload.id;
      const uploadAction = isQuickTrip
        ? uploadQuickTripImages({ id: savedId, files: pendingImageFiles })
        : uploadPackageImages({ id: savedId, files: pendingImageFiles });
      await dispatch(uploadAction);
    }

    navigate("/packages");
  };

  const renderStep = () => {
    const key = stepTitles[step];
    if (key === "Basic Information")
      return (
        <BasicInfoStep
          form={form}
          setForm={setForm}
          isEdit={isEdit}
          errors={stepErrors}
          pendingImageFiles={pendingImageFiles}
          setPendingImageFiles={setPendingImageFiles}
        />
      );
    if (key === "Details") return <DetailsStep form={form} setForm={setForm} errors={stepErrors} />;
    if (key === "Components")
      return (
        <ComponentsStep
          entries={entries}
          setEntries={setEntries}
          existingLinks={existingLinks}
          onRemoveExisting={handleRemoveExisting}
          errors={stepErrors}
        />
      );
    if (key === "Pricing") return <PricingStep form={form} setForm={setForm} errors={stepErrors} />;
    return null;
  };

  const isLastStep = step === stepTitles.length - 1;

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />
        <div className="listContainer">
          <Title level={3}>
            {isEdit ? "Edit" : "Create"} {isQuickTrip ? "Quick Trip" : "Package"}
          </Title>

          <Steps
            current={step}
            items={stepTitles.map((title) => ({ title }))}
            style={{ marginBottom: 32 }}
          />

          {loadingRecord ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div style={{ maxWidth: 900 }}>{renderStep()}</div>

              <Row justify="space-between" style={{ marginTop: 32, maxWidth: 900 }}>
                <Col>
                  {step > 0 && (
                    <Button
                      onClick={() => {
                        setStepErrors({});
                        setStep((s) => s - 1);
                      }}
                    >
                      Back
                    </Button>
                  )}
                </Col>
                <Col>
                  {!isLastStep ? (
                    <Button type="primary" onClick={() => goToStep(step + 1)}>
                      Continue
                    </Button>
                  ) : (
                    <Button type="primary" loading={actionLoading} onClick={handleSubmit}>
                      {isEdit ? "Save Changes" : `Create ${isQuickTrip ? "Quick Trip" : "Package"}`}
                    </Button>
                  )}
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageWizard;
